import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import useClientContext from "../../../../../context/useClientContext";
import { useEnhancedBiApiClientProvider } from "../contexts/ApiClientProviderContext";
import type { AppDispatch } from "../store/store";
import { setEditReport } from "../store/thunks/currentReportThunks";
import { clearVersionHistory } from "../store/thunks/versionHistoryStateThunks";
import { createCurrentReportAction } from "../store/utils/storeUtils";
import { getExistingReportWithAuth, manageReportSwitching } from "../utilities/defineReportDataHelper";

export const useDefineEditReportData = (id: string) => {
  const { clientCode } = useClientContext();
  const { getReport, getReportAuthorizationInfo } = useEnhancedBiApiClientProvider();
  const dispatch: AppDispatch = useDispatch();
  const [showReportNotFound, setShowReportNotFound] = useState(false);
  const [isSwitchingReport, setIsSwitchingReport] = useState(false);
  const cachedReportIdRef = useRef<string | undefined>(undefined);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const isSwitchingReport = !!cachedReportIdRef.current && cachedReportIdRef.current !== id;
    cachedReportIdRef.current = id;

    if (id === undefined) {
      return;
    }

    let isCurrent = true;
    const execute = async () => {
      dispatch(clearVersionHistory());
      setIsFetching(true);
      await getExistingReportWithAuth(
        getReport.bind(null, id, "edit"),
        undefined,
        getReportAuthorizationInfo.bind(null, id),
        () => isCurrent && setShowReportNotFound(true),
        (report) => isCurrent && dispatch(setEditReport(report)),
        (authInfo) => isCurrent && dispatch(createCurrentReportAction("setAuthorizationInfo", authInfo))
      );

      if (isCurrent) {
        setIsFetching(false);
      }
    };
    manageReportSwitching(isSwitchingReport, setIsSwitchingReport, execute);

    return () => {
      isCurrent = false;
    };

    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [id, clientCode, getReport, getReportAuthorizationInfo]);

  return { showReportNotFound, isSwitchingReport, isFetching };
};
